import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["row", "checkboxTemplate", "checkbox", "toolbar", "count", "actions", "selectAllLabel", "deselectAllLabel"]
  static values = { active: Boolean }

  enter({ params: { id } }) {
    this.activate()

    const checkbox = this.checkboxTargets.find((checkbox) => checkbox.value === String(id))
    if (checkbox) {
      checkbox.checked = true
      checkbox.focus()
    }
    this.update()
  }

  selectAll() {
    this.activate()
    this.visibleCheckboxes.forEach((checkbox) => (checkbox.checked = true))
    this.update()
  }

  toggleAll() {
    if (this.allVisibleChecked) {
      this.checkboxTargets.forEach((checkbox) => (checkbox.checked = false))
      this.update()
    } else {
      this.selectAll()
    }
  }

  exit() {
    this.activeValue = false
  }

  exitOther({ target }) {
    if (target !== this.element) this.exit()
  }

  // Escape inside a popup or dialog closes that layer, not the selection.
  exitOnEscape({ target }) {
    if (!target.closest("dialog")) this.exit()
  }

  deselectHidden() {
    this.checkboxTargets.filter((checkbox) => !checkbox.checkVisibility()).forEach((checkbox) => (checkbox.checked = false))
    this.update()
  }

  update() {
    if (this.hasToolbarTarget) {
      const count = this.checkboxTargets.filter((checkbox) => checkbox.checked).length

      this.toolbarTarget.hidden = !this.activeValue
      this.countTarget.textContent = count
      this.selectAllLabelTarget.classList.toggle("invisible", this.allVisibleChecked)
      this.deselectAllLabelTarget.classList.toggle("invisible", !this.allVisibleChecked)
      this.actionsTarget.querySelectorAll("button").forEach((button) => (button.disabled = count === 0))
    }
  }

  activeValueChanged(active) {
    this.rowTargets.forEach((row) => (active ? this.addCheckbox(row) : this.removeCheckbox(row)))
    this.update()
  }

  rowTargetConnected(row) {
    if (this.activeValue) this.addCheckbox(row)
  }

  checkboxTargetDisconnected() {
    this.update()
  }

  activate() {
    if (!this.activeValue) {
      this.dispatch("enter")
      this.activeValue = true
    }
    this.rowTargets.forEach((row) => this.addCheckbox(row))
  }

  addCheckbox(row) {
    if (this.hasCheckboxTemplateTarget && !this.checkboxIn(row)) {
      const control = this.checkboxTemplateTarget.content.firstElementChild.cloneNode(true)
      const checkbox = control.querySelector("input")

      checkbox.value = row.dataset.taskId
      checkbox.ariaLabel = `Select ${row.querySelector(".action-item__link")?.title ?? "task"}`
      row.prepend(control)
    }
  }

  removeCheckbox(row) {
    this.checkboxIn(row)?.closest("label").remove()
  }

  checkboxIn(row) {
    return this.checkboxTargets.find((checkbox) => row.contains(checkbox))
  }

  // Rows inside a closed section stay in the DOM; select only what the user can see.
  get visibleCheckboxes() {
    return this.checkboxTargets.filter((checkbox) => checkbox.checkVisibility())
  }

  get allVisibleChecked() {
    const visible = this.visibleCheckboxes
    return visible.length > 0 && visible.every((checkbox) => checkbox.checked)
  }
}
